/**
 * Group / sort BUDOVA WF rows so objects sharing the same rozvaděč sit together.
 * Empty rozvaděč is one group at the end („Bez rozvaděče“).
 */

export const BUDOVA_PRIPOJENI_EMPTY_ROZVADEC_LABEL = 'Bez rozvaděče';

export interface BudovaPripojeniRozvadecSortItem {
  /** Stable group identity: `id:<objectId>`, `label:<normalized>`, or empty. */
  groupKey: string;
  /** Display label used to order named groups (cs locale). */
  sortLabel: string;
  address: string;
  cp: string;
  parcelNumber: string;
  phaseLabel: string;
  id: string;
}

export function resolveRozvadecGroupKey(
  rozvadecObjectId: string | null | undefined,
  rozvadecLabel: string | null | undefined,
): string {
  const id = (rozvadecObjectId ?? '').trim();
  if (id) return `id:${id}`;
  const label = (rozvadecLabel ?? '').trim();
  if (label) return `label:${label.toLocaleLowerCase('cs')}`;
  return '';
}

export function formatBudovaPripojeniRozvadecCell(
  label: string | null | undefined,
): string {
  const t = (label ?? '').trim();
  return t || BUDOVA_PRIPOJENI_EMPTY_ROZVADEC_LABEL;
}

export function budovaPripojeniRozvadecSortItem(fields: {
  rozvadecObjectId?: string | null;
  rozvadecLabel?: string | null;
  address?: string | null;
  cp?: string | null;
  parcelNumber?: string | null;
  phaseLabel?: string | null;
  id: string;
}): BudovaPripojeniRozvadecSortItem {
  const label = (fields.rozvadecLabel ?? '').trim();
  return {
    groupKey: resolveRozvadecGroupKey(fields.rozvadecObjectId, label),
    sortLabel: label,
    address: (fields.address ?? '').trim(),
    cp: (fields.cp ?? '').trim(),
    parcelNumber: (fields.parcelNumber ?? '').trim(),
    phaseLabel: (fields.phaseLabel ?? '').trim(),
    id: fields.id,
  };
}

/** Empty groups always last; named groups by label, then identity. */
export function compareRozvadecGroups(
  a: Pick<BudovaPripojeniRozvadecSortItem, 'groupKey' | 'sortLabel'>,
  b: Pick<BudovaPripojeniRozvadecSortItem, 'groupKey' | 'sortLabel'>,
  groupDir: 1 | -1 = 1,
): number {
  const aEmpty = !a.groupKey;
  const bEmpty = !b.groupKey;
  if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
  if (aEmpty) return 0;
  const labelCmp = a.sortLabel.localeCompare(b.sortLabel, 'cs', {
    sensitivity: 'base',
    numeric: true,
  });
  if (labelCmp !== 0) return groupDir * labelCmp;
  return groupDir * a.groupKey.localeCompare(b.groupKey, 'cs');
}

function compareCsEmptyLast(a: string, b: string): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, 'cs', { sensitivity: 'base', numeric: true });
}

/** Address → č.p. → parcel → fáze → object id (stable). */
export function compareWithinRozvadecGroup(
  a: BudovaPripojeniRozvadecSortItem,
  b: BudovaPripojeniRozvadecSortItem,
): number {
  const addr = compareCsEmptyLast(a.address, b.address);
  if (addr !== 0) return addr;
  const cp = compareCsEmptyLast(a.cp, b.cp);
  if (cp !== 0) return cp;
  const parcel = compareCsEmptyLast(a.parcelNumber, b.parcelNumber);
  if (parcel !== 0) return parcel;
  const phase = compareCsEmptyLast(a.phaseLabel, b.phaseLabel);
  if (phase !== 0) return phase;
  return a.id.localeCompare(b.id, 'cs');
}

export function compareBudovaPripojeniRozvadecThenAddress(
  a: BudovaPripojeniRozvadecSortItem,
  b: BudovaPripojeniRozvadecSortItem,
  groupDir: 1 | -1 = 1,
): number {
  const groupCmp = compareRozvadecGroups(a, b, groupDir);
  if (groupCmp !== 0) return groupCmp;
  return compareWithinRozvadecGroup(a, b);
}

export function sortBudovaPripojeniRowsByRozvadec<T extends {
  id: string;
  address: string;
  cp: string;
  parcelNumber?: string;
  phaseLabel: string;
  rozvadecLabel: string;
  rozvadecObjectId?: string | null;
}>(rows: readonly T[], groupDir: 1 | -1 = 1): T[] {
  return [...rows].sort((a, b) =>
    compareBudovaPripojeniRozvadecThenAddress(
      budovaPripojeniRozvadecSortItem({
        id: a.id,
        address: a.address,
        cp: a.cp,
        parcelNumber: a.parcelNumber,
        phaseLabel: a.phaseLabel,
        rozvadecLabel: a.rozvadecLabel,
        rozvadecObjectId: a.rozvadecObjectId,
      }),
      budovaPripojeniRozvadecSortItem({
        id: b.id,
        address: b.address,
        cp: b.cp,
        parcelNumber: b.parcelNumber,
        phaseLabel: b.phaseLabel,
        rozvadecLabel: b.rozvadecLabel,
        rozvadecObjectId: b.rozvadecObjectId,
      }),
      groupDir,
    ),
  );
}
